import { Slot } from "@radix-ui/react-slot";
import { ChevronRight, ExternalLink, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import * as React from "react";
import { cx } from "../internal/cx.js";

/**
 * Sidebar — application-shell sidebar with expanded and collapsed states.
 *
 * Use as the persistent navigation column in dashboards and authenticated
 * surfaces. The sidebar can be:
 *   - 'expanded' — full-width with text labels (the default desktop state).
 *   - 'collapsed' — icon-only rail (saves horizontal space on small desktops).
 *
 * On mobile, prefer a bottom/footer navigation pattern for primary
 * destinations. A Sheet can host secondary or deep navigation, but the
 * Sidebar itself is a desktop app-shell pattern.
 *
 * The component is dumb-and-controlled. Parent owns:
 *   - the active route (`activeId`)
 *   - the expanded/collapsed state (`collapsed`, `onCollapsedChange`)
 *   - any onClick handlers / link rendering for items.
 *
 * Items can be provided imperatively (compose <SidebarItem>) or
 * declaratively via the `groups` data prop (faster for static nav lists).
 */

export type SidebarState = "expanded" | "collapsed";

export interface SidebarItemConfig {
  id: string;
  label: React.ReactNode;
  /** Lucide-style icon. Required when sidebar is collapsed. */
  icon?: React.ReactNode;
  /** Optional badge or count rendered on the trailing edge. */
  badge?: React.ReactNode;
  /** Disable interaction. */
  disabled?: boolean;
  /** Click / navigation handler. */
  onClick?: () => void;
  /** Render as an asChild (e.g. a NextLink). */
  href?: string;
  /**
   * Marks a cross-app / external destination. Renders a trailing
   * "opens in a new tab" affordance next to the label (the dashboard-v2
   * "Your Benefits Wallet" pattern). Visual only — the host wires the
   * actual `target="_blank"` link via `onClick`/`href`.
   */
  external?: boolean;
  /** Optional nested items. Renders as an expandable section when present. */
  items?: SidebarItemConfig[];
}

export interface SidebarGroup {
  id: string;
  label?: React.ReactNode;
  /** Render a divider rule above this group (separates destination clusters). */
  divider?: boolean;
  /** Render this group's items a touch lighter (secondary destinations). */
  secondary?: boolean;
  items: SidebarItemConfig[];
}

interface SidebarContextValue {
  collapsed: boolean;
}

const SidebarContext = React.createContext<SidebarContextValue>({ collapsed: false });

export function useSidebar(): SidebarContextValue {
  return React.useContext(SidebarContext);
}

export interface SidebarProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * Visual layout mode.
   * - `fixed` (default) — the shipping owner-dashboard-v2 pattern: a fixed
   *   ~280px column with no collapse rail and no right border (the body panel
   *   owns the seam). Blue-50 active pill.
   * - `desktop` — bordered rail that supports the collapse toggle.
   * - `mobile` — legacy/documentation-only.
   */
  variant?: "fixed" | "desktop" | "mobile";
  /**
   * Groups pinned to the bottom of the column (e.g. Settings), rendered in a
   * separate nav landmark via `margin-top: auto`. Only used with the `groups`
   * data-driven API.
   */
  bottomGroups?: SidebarGroup[];
  /** Accessible name for the bottom nav landmark. Defaults to `"Secondary"`. */
  bottomNavLabel?: string;
  /** Whether the sidebar is in the collapsed (icon-only) state. */
  collapsed?: boolean;
  /** Notified when the user toggles via the built-in collapse button. */
  onCollapsedChange?: (next: boolean) => void;
  /** Hide the built-in collapse toggle (e.g. when the host manages it). */
  hideCollapseToggle?: boolean;
  /** Header slot — usually a logo + workspace switcher. */
  header?: React.ReactNode;
  /** Footer slot — pinned to the bottom (user menu, settings). */
  footer?: React.ReactNode;
  /** Data-driven nav config (alternative to composing children). */
  groups?: SidebarGroup[];
  /** Active item id — applied when groups are passed. */
  activeId?: string;
  /**
   * Accessible name for the inner navigation landmark. Defaults to
   * `"Primary"`. Override when multiple sidebars coexist on a page so each
   * `<nav>` landmark has a unique accessible name (axe's `landmark-unique`).
   */
  navLabel?: string;
}

export const Sidebar = React.forwardRef<HTMLElement, SidebarProps>(function Sidebar(
  {
    activeId,
    bottomGroups,
    bottomNavLabel = "Secondary",
    children,
    className,
    collapsed = false,
    footer,
    groups,
    header,
    hideCollapseToggle,
    navLabel = "Primary",
    onCollapsedChange,
    variant = "fixed",
    ...props
  },
  ref,
) {
  // The fixed app-shell column has no collapse rail; force the toggle off
  // regardless of the caller so the documented default stays true to the
  // shipping dashboard-v2 nav.
  const collapseHidden = variant === "fixed" ? true : hideCollapseToggle;
  return (
    <SidebarContext.Provider value={{ collapsed }}>
      <aside
        ref={ref}
        className={cx(
          "cw-v2-sidebar",
          `cw-v2-sidebar--${variant}`,
          collapsed && "cw-v2-sidebar--collapsed",
          className,
        )}
        data-state={collapsed ? "collapsed" : "expanded"}
        {...props}
      >
        {header || !collapseHidden ? (
          <div className="cw-v2-sidebar__header">
            {header ? <div className="cw-v2-sidebar__header-slot">{header}</div> : null}
            {collapseHidden ? null : (
              <button
                type="button"
                className="cw-v2-sidebar__collapse"
                aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
                aria-pressed={collapsed}
                onClick={() => onCollapsedChange?.(!collapsed)}
              >
                {collapsed ? (
                  <PanelLeftOpen className="cw-v2-sidebar__collapse-icon" aria-hidden />
                ) : (
                  <PanelLeftClose className="cw-v2-sidebar__collapse-icon" aria-hidden />
                )}
              </button>
            )}
          </div>
        ) : null}
        <nav className="cw-v2-sidebar__nav" aria-label={navLabel}>
          {groups
            ? groups.map((group) => (
                <SidebarGroupBlock key={group.id} label={group.label} divider={group.divider}>
                  {group.items.map((item) => (
                    <SidebarItemFromConfig
                      key={item.id}
                      item={item}
                      activeId={activeId}
                      secondary={group.secondary}
                    />
                  ))}
                </SidebarGroupBlock>
              ))
            : children}
        </nav>
        {bottomGroups && bottomGroups.length > 0 ? (
          <nav className="cw-v2-sidebar__nav-bottom" aria-label={bottomNavLabel}>
            {bottomGroups.map((group) => (
              <SidebarGroupBlock key={group.id} label={group.label} divider={group.divider}>
                {group.items.map((item) => (
                  <SidebarItemFromConfig
                    key={item.id}
                    item={item}
                    activeId={activeId}
                    secondary={group.secondary}
                  />
                ))}
              </SidebarGroupBlock>
            ))}
          </nav>
        ) : null}
        {footer ? <div className="cw-v2-sidebar__footer">{footer}</div> : null}
      </aside>
    </SidebarContext.Provider>
  );
});
Sidebar.displayName = "Sidebar";

export const SidebarGroupBlock = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    label?: React.ReactNode;
    /** Render a divider rule above the group. */
    divider?: boolean;
  }
>(function SidebarGroupBlock({ children, className, label, divider, ...props }, ref) {
  const { collapsed } = useSidebar();
  return (
    <>
      {divider ? <hr className="cw-v2-sidebar__divider" /> : null}
      <div ref={ref} className={cx("cw-v2-sidebar__group", className)} {...props}>
        {label && !collapsed ? <div className="cw-v2-sidebar__group-label">{label}</div> : null}
        <ul className="cw-v2-sidebar__list">{children}</ul>
      </div>
    </>
  );
});
SidebarGroupBlock.displayName = "SidebarGroupBlock";

export interface SidebarItemProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  /** Item label. */
  label: React.ReactNode;
  /** Leading icon. Required when sidebar is collapsed. */
  icon?: React.ReactNode;
  /** Trailing badge. */
  badge?: React.ReactNode;
  /** Whether this item is the active route. */
  active?: boolean;
  /** Lighter weight for secondary destinations. */
  secondary?: boolean;
  /** Render a trailing "opens in a new tab" affordance (external destination). */
  external?: boolean;
  /** Optional nested items (renders an expandable section). */
  children?: React.ReactNode;
  /** Render as the immediate child (e.g. a router <Link>). */
  asChild?: boolean;
}

export const SidebarItem = React.forwardRef<HTMLButtonElement, SidebarItemProps>(
  function SidebarItem(
    {
      active,
      asChild,
      badge,
      children,
      className,
      external,
      icon,
      label,
      secondary,
      type = "button",
      ...props
    },
    ref,
  ) {
    const { collapsed } = useSidebar();
    const Comp: React.ElementType = asChild ? Slot : "button";
    const buttonProps = asChild ? {} : { type };
    return (
      <li className="cw-v2-sidebar__item-wrap">
        <Comp
          ref={ref}
          aria-current={active ? "page" : undefined}
          // When collapsed the label span is hidden, so without aria-label
          // the button has no accessible name and axe flags button-name.
          aria-label={collapsed && typeof label === "string" ? label : undefined}
          className={cx(
            "cw-v2-sidebar__item",
            active && "cw-v2-sidebar__item--active",
            secondary && "cw-v2-sidebar__item--secondary",
            collapsed && "cw-v2-sidebar__item--collapsed",
            className,
          )}
          {...buttonProps}
          {...props}
        >
          {icon ? (
            <span className="cw-v2-sidebar__item-icon" aria-hidden>
              {icon}
            </span>
          ) : null}
          {!collapsed ? <span className="cw-v2-sidebar__item-label">{label}</span> : null}
          {!collapsed && badge ? <span className="cw-v2-sidebar__item-badge">{badge}</span> : null}
          {!collapsed && external ? (
            <ExternalLink
              className="cw-v2-sidebar__item-external"
              aria-label="Opens in a new tab"
            />
          ) : null}
        </Comp>
        {children && !collapsed ? <ul className="cw-v2-sidebar__subitems">{children}</ul> : null}
      </li>
    );
  },
);
SidebarItem.displayName = "SidebarItem";

/** Internal renderer for the `groups` data-driven API. */
function SidebarItemFromConfig({
  item,
  activeId,
  secondary,
}: {
  item: SidebarItemConfig;
  activeId?: string;
  secondary?: boolean;
}) {
  const isActive = item.id === activeId;
  const [expanded, setExpanded] = React.useState(
    () => isActive || (item.items?.some((sub) => sub.id === activeId) ?? false),
  );
  if (item.items && item.items.length > 0) {
    return (
      <li className="cw-v2-sidebar__item-wrap">
        <button
          type="button"
          className={cx("cw-v2-sidebar__item", isActive && "cw-v2-sidebar__item--active")}
          aria-expanded={expanded}
          onClick={() => setExpanded((prev) => !prev)}
        >
          {item.icon ? (
            <span className="cw-v2-sidebar__item-icon" aria-hidden>
              {item.icon}
            </span>
          ) : null}
          <span className="cw-v2-sidebar__item-label">{item.label}</span>
          <ChevronRight
            aria-hidden
            className={cx(
              "cw-v2-sidebar__item-chevron",
              expanded && "cw-v2-sidebar__item-chevron--expanded",
            )}
          />
        </button>
        {expanded ? (
          <ul className="cw-v2-sidebar__subitems">
            {item.items.map((sub) => (
              <SidebarItemFromConfig key={sub.id} item={sub} activeId={activeId} />
            ))}
          </ul>
        ) : null}
      </li>
    );
  }
  return (
    <SidebarItem
      active={isActive}
      badge={item.badge}
      disabled={item.disabled}
      external={item.external}
      icon={item.icon}
      label={item.label}
      onClick={item.onClick}
      secondary={secondary}
    />
  );
}
